import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {User} from '../models/user';
import {AuthService} from './auth.service';
import {FirestoreService} from './firestore-utils/firestore.service';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private currentUser$ = new BehaviorSubject<User>(null);
  private currentUserSub = new Subscription();

  constructor(
    private firestoreService: FirestoreService,
    private authService: AuthService
  ) {
    this.trackCurrentUser();
  }

  // --------------------------------------------------------------------------------------------------------
  // Current User Functions
  // --------------------------------------------------------------------------------------------------------

  private trackCurrentUser() {
    this.authService.getAuthUser().subscribe((authUser) => {
      this.currentUserSub.unsubscribe();
      if (!authUser) { return this.currentUser$.next(null); }

      this.currentUserSub = this.observeUser(authUser.uid).subscribe(user => this.currentUser$.next(user));
    });
  }

  getCurrentUser(): Observable<User> {
    return this.currentUser$.asObservable();
  }

  // --------------------------------------------------------------------------------------------------------
  // Generic Firebase Functions
  // --------------------------------------------------------------------------------------------------------

  getUser(uid: string): Promise<User> {
    return this.firestoreService.getRecord('users', uid) as Promise<User>;
  }

  getUserByEmail(email: string): Promise<User> {
    return this.firestoreService.getRecordByProperty('users', 'email', '==', email) as Promise<User>;
  }

  getUserByShop(shopId: string): Promise<User> {
    return this.firestoreService.getRecordByProperty('users', 'shopId', '==', shopId) as Promise<User>;
  }

  getUsers(): Promise<User[]> {
    return this.firestoreService.getCollection('users');
  }

  addUser(user: User): Promise<User> {
    return this.firestoreService.addOrUpdateRecord('users', {...user}) as Promise<User>;
  }

  updateUser(user: User): Promise<User> {
    return this.firestoreService.addOrUpdateRecord('users', JSON.parse(JSON.stringify(user))) as Promise<User>;
  }

  removeUser(uid: string) {
    return this.firestoreService.removeRecord('users', uid);
  }

  observeUser(uid: string): Observable<User> {
    return this.firestoreService.observeRecord('users', uid);
  }

  observeUsers(): Observable<User[]> {
    return this.firestoreService.observeCollection('users');
  }

  isAuthFullyRegistered(authUserId: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.getUser(authUserId).then((user) => resolve(user ? true : false));
    });
  }

}
